import { Injectable } from '@angular/core';

import { ApiService } from './api.service';
import { AuthApiService } from './auth-api.service';
import {  RegisterUserRequest } from '../types/auth.types';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class UserRoleService {

  constructor(
    private apiService: ApiService,
    private authApiService: AuthApiService
  ) {}

  /**
   * Get user account with enhanced error handling
   * Recommended method for new implementations
   */
  getUserAccountWithErrorHandling(): Observable<any> {
    console.log('🔍 [UserRoleService] Looking up user account with error handling');
    return this.authApiService.getUserAccount().pipe(
      map(result => {
        if (result.success && result.data) {
          console.log(`✅ [UserRoleService] API response status: ${result.data.status}`);
          // Return the full response from the API
          return result.data;
        } else {
          console.warn('⚠️ [UserRoleService] API call failed');
          return {
            status: 'unregistered',
            message: 'Failed to lookup user account',
            user: null
          };
        }
      }),
      catchError(error => {
        console.error('❌ [UserRoleService] Error during user account lookup:', error);
        
        // Check if this is an HTTP error (non-200 status)
        if (error.isHttpError) {
          // HTTP errors should be propagated as login errors, not treated as unregistered
          throw error;
        }
        
        // For other errors, return unregistered status
        return [{
          status: 'unregistered',
          message: 'Error occurred during user lookup',
          user: null
        }];
      })
    );
  }

  /**
   * Register user with enhanced error handling
   */
  registerUserWithErrorHandling(userData: RegisterUserRequest): Observable<any> {
    console.log('🚀 [UserRoleService] Registering user with error handling');
    
    return this.authApiService.registerUser(userData).pipe(
      map(result => {
        if (result.success && result.data) {
          console.log('✅ [UserRoleService] User registered successfully:', result.data.user.id);
          return result.data;
        } else {
          throw new Error('Registration failed');
        }
      })
    );
  }

  /**
   * Check if user exists with enhanced error handling
   */
  checkUserExistsWithErrorHandling(mobile?: string, email?: string): Observable<boolean> {
    console.log('🔍 [UserRoleService] Checking if user exists');
    
    return this.authApiService.checkUserExists().pipe(
      map(result => {
        if (result.success) {
          const exists = result.data || false;
          console.log(`${exists ? '✅' : '❌'} [UserRoleService] User exists: ${exists}`);
          return exists;
        } else {
          console.warn('⚠️ [UserRoleService] Could not check user existence');
          return false;
        }
      })
    );
  }

  /**
   * Get user-friendly error message from API error
   */
  getErrorMessage(error: any): string {
    return this.authApiService.getErrorMessage(error);
  }

  /**
   * Check if error requires login redirect
   */
  shouldRedirectToLogin(error: any): boolean {
    return this.authApiService.shouldRedirectToLogin(error);
  }

  /**
   * Validate and prepare registration data
   */
  prepareRegistrationData(data: {
    mobile: string;
    email?: string;
    name?: string;
    type?: 'individual' | 'agency';
    emailVerified?: boolean;
    mobileVerified?: boolean;
  }): { isValid: boolean; data?: RegisterUserRequest; errors?: string[] } {
    return this.authApiService.prepareRegistrationData(data);
  }
}